import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AdminUser } from '../types/user.types';
import { useAuth } from './AuthContext';

interface PermissionContextType {
    currentAdmin: AdminUser | null;
    getCurrentAdmin: () => AdminUser | null;
    isSuperAdmin: () => boolean;
    hasPermission: (permission: string) => boolean;
    hasAnyPermission: (permissions: string[]) => boolean;
    hasAllPermissions: (permissions: string[]) => boolean;
    getPermissions: () => string[];
    updateAdminDetails: (admin: AdminUser) => void;
    clearAdminDetails: () => void;
    hasRoutePermission: (routePath: string) => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermission = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermission must be used within a PermissionProvider');
    }
    return context;
};

interface PermissionProviderProps {
    children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
    const [currentAdmin, setCurrentAdmin] = useState<AdminUser | null>(() => {
        const stored = localStorage.getItem('userDetail');
        try {
            return stored ? JSON.parse(stored) : null;
        } catch (e) {
            return null;
        }
    });
    const { user, accessToken, isInitializing } = useAuth();

    useEffect(() => {
        if (isInitializing) {
            return;
        }

        if (!accessToken || !user) {
            // Only clear if explicitly not authenticated
            if (!localStorage.getItem('accessToken')) {
                setCurrentAdmin(null);
            }
        } else {
            setCurrentAdmin(user);
        }
    }, [user, accessToken, isInitializing]);

    const getCurrentAdmin = useCallback((): AdminUser | null => {
        if (currentAdmin) return currentAdmin;

        // Fallback to localStorage if state is null but user is potentially logged in
        const stored = localStorage.getItem('userDetail');
        const token = localStorage.getItem('accessToken');
        if (token && stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                return null;
            }
        }
        return null;
    }, [currentAdmin]);

    const isSuperAdmin = useCallback((): boolean => {
        const admin = getCurrentAdmin();
        return admin?.role === 'super_admin';
    }, [getCurrentAdmin]);

    const hasUserPermission = useCallback((permission: string): boolean => {
        // Static permissions for regular users
        const userPermissions = [
            'profile',
            'change-password'
        ];
        return userPermissions.includes(permission);
    }, []);

    const hasPermission = useCallback((permission: string): boolean => {
        const userType = localStorage.getItem('userType');

        // For regular users, check static permissions
        if (userType === 'user') {
            return hasUserPermission(permission);
        }

        const admin = getCurrentAdmin();

        // Super admin has all permissions
        if (isSuperAdmin()) {
            return true;
        }

        // Get menu permissions from either field name (for backward compatibility)
        const menuPermissions = admin?.menu_permission || (admin as any)?.menuPermissions || (admin as any)?.menuPermission || [];

        // If admin has "*" permission, they have access to all menus
        if (menuPermissions.includes('*')) {
            return true;
        }

        // Check if admin has the specific permission
        return menuPermissions.includes(permission) || false;
    }, [getCurrentAdmin, isSuperAdmin, hasUserPermission]);

    const hasAnyPermission = useCallback((permissions: string[]): boolean => {
        return permissions.some(permission => hasPermission(permission));
    }, [hasPermission]);

    const hasAllPermissions = useCallback((permissions: string[]): boolean => {
        return permissions.every(permission => hasPermission(permission));
    }, [hasPermission]);

    const getPermissions = useCallback((): string[] => {
        const userType = localStorage.getItem('userType');

        // For regular users, return static permissions
        if (userType === 'user') {
            return [
                'profile',
                'change-password',
            ];
        }

        const admin = getCurrentAdmin();
        // Get menu permissions from either field name (for backward compatibility)
        return admin?.menu_permission || (admin as any)?.menuPermissions || (admin as any)?.menuPermission || [];
    }, [getCurrentAdmin]);

    const updateAdminDetails = useCallback((admin: AdminUser): void => {
        setCurrentAdmin(admin);
        localStorage.setItem('userDetail', JSON.stringify(admin));
    }, []);

    const clearAdminDetails = useCallback((): void => {
        setCurrentAdmin(null);
        localStorage.removeItem('userDetail');
    }, []);

    const hasUserRoutePermission = useCallback((routePath: string): boolean => {
        // Static route permissions for regular users
        const userRoutePermissions = [
            'profile',
            'change-password',
        ];

        return userRoutePermissions.some(perm => {
            if (perm.includes(':id')) {
                const regex = new RegExp('^' + perm.replace(':id', '[^/]+') + '$');
                return regex.test(routePath);
            }
            return perm === routePath;
        });
    }, []);

    const hasRoutePermission = useCallback((routePath: string): boolean => {
        const userType = localStorage.getItem('userType');

        // For regular users, check static route permissions
        if (userType === 'user') {
            return hasUserRoutePermission(routePath);
        }

        const isSuper = isSuperAdmin();
        if (isSuper) return true;

        // Extract the main part of the route (e.g., 'users' from 'users/add')
        const pathSegments = routePath.split('/');
        const mainPath = pathSegments[0];

        // Specific routes that are always allowed for any authenticated admin
        if (['dashboard', 'profile', 'change-password'].includes(mainPath)) {
            return true;
        }

        // Map main path to the base permission ID
        const permissionMap: { [key: string]: string } = {
            'admin': 'admin',
            'users': 'users',
            'category': 'category',
            'product': 'product',
            'product-ratings': 'product-ratings',
            'coupons': 'coupons',
            'cms': 'cms',
            'notifications': 'notifications',
            'settings': 'settings',
            'tax': 'tax',
            'delivery-charge': 'deliveryCharge',
            'category-value': 'categoryValue',
            'orders': 'orders',
        };

        const basePermission = permissionMap[mainPath];
        if (!basePermission) return true; // Default to allow if not explicitly mapped

        // Check for specific sub-route permissions
        if (pathSegments.includes('add')) {
            return hasPermission(`${basePermission}.add`);
        }
        if (pathSegments.includes('edit')) {
            return hasPermission(`${basePermission}.update`);
        }
        if (pathSegments.includes('view')) {
            // For view routes, we check the base permission which represents 'view'
            return hasPermission(basePermission);
        }

        // Default back to base permission check
        return hasPermission(basePermission);
    }, [hasPermission, isSuperAdmin, hasUserRoutePermission]);

    return (
        <PermissionContext.Provider
            value={{
                currentAdmin,
                getCurrentAdmin,
                isSuperAdmin,
                hasPermission,
                hasAnyPermission,
                hasAllPermissions,
                getPermissions,
                updateAdminDetails,
                clearAdminDetails,
                hasRoutePermission,
            }}
        >
            {children}
        </PermissionContext.Provider>
    );
};

export { PermissionContext };
